// src/components/admin/DiagnitariesBook.tsx
// DROP 35B — Full Dignitaries Address Book
// Admin: add/edit/delete + contact via WhatsApp/Email
// Public: view approved dignitaries only

import React, { useState, useEffect, useCallback } from 'react';
import {
  Crown, Plus, Edit2, Trash2, Phone, Mail,
  Search, Filter, Eye, EyeOff, MapPin,
  MessageCircle, CheckCircle, AlertCircle,
  RefreshCw, ExternalLink
} from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      '#070D0A',
  surface: '#0E1812',
  card:    '#121E16',
  border:  '#1C2E22',
  text:    '#EDE8DC',
  muted:   'rgba(237,232,220,0.55)',
  dim:     'rgba(237,232,220,0.35)',
  green:   '#0F5132',
  gold:    '#C9A84C',
  goldLt:  '#E2C97E',
  red:     '#ef4444',
  blue:    '#60a5fa',
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface Dignitary {
  id: string;
  name: string;
  title: string;
  rank: string;
  position: string;
  category: string;
  village?: string;
  photo?: string;
  bio?: string;
  phone?: string;
  email?: string;
  whatsapp?: string;
  facebook?: string;
  twitter?: string;
  isPublic: boolean;
  displayOrder: number;
  addedAt?: string;
}

interface Props {
  isAdmin?: boolean;
  currentUser?: any;
}

const CATEGORIES = [
  'All', 'Royal House', 'Traditional Chiefs', 'Government Officials',
  'Religious Leaders', 'Community Leaders', 'Diaspora Leaders',
  'Special Guests', 'Corporate Partners', 'Media & Press'
];

const RANKS = [
  'HRH', 'HRE', 'HE', 'Chief', 'Elder', 'Engr.', 'Dr.', 'Prof.',
  'Barr.', 'Alhaji', 'Pastor', 'Rev.', 'Deacon', 'Mr.', 'Mrs.', 'Ms.'
];

const EMPTY_FORM = {
  name: '', title: '', rank: 'Chief', position: '', category: 'Traditional Chiefs',
  village: '', photo: '', bio: '', phone: '', email: '', whatsapp: '',
  facebook: '', twitter: '', isPublic: true, displayOrder: 0,
};

// ── Helper: Input style ───────────────────────────────────────────────────────
const inp: React.CSSProperties = {
  width: '100%', background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 7, padding: '9px 12px', color: C.text, fontSize: 13,
  outline: 'none', boxSizing: 'border-box',
};

// ── Component ─────────────────────────────────────────────────────────────────
const DiagnitariesBook: React.FC<Props> = ({ isAdmin = false, currentUser }) => {
  const [dignitaries, setDignitaries] = useState<Dignitary[]>([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [catFilter, setCatFilter]     = useState('All');
  const [showForm, setShowForm]       = useState(false);
  const [editTarget, setEditTarget]   = useState<Dignitary | null>(null);
  const [form, setForm]               = useState({ ...EMPTY_FORM });
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState<{ type: 'ok'|'err'; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [viewMode, setViewMode]       = useState<'grid'|'list'>('grid');

  const flash = (type: 'ok'|'err', text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const token = () =>
    localStorage.getItem('erue_auth_token') ||
    sessionStorage.getItem('erue_auth_token') || '';

  const loadDignitaries = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isAdmin
        ? '/api/admin/dignitaries-full'
        : '/api/public/dignitaries';
      const headers: Record<string, string> = isAdmin
        ? { Authorization: `Bearer ${token()}` }
        : {};
      const r = await fetch(endpoint, { headers });
      const d = await r.json();
      if (d.success) {
        setDignitaries(d.dignitaries || []);
      }
    } catch {
      flash('err', 'Failed to load dignitaries');
    }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { loadDignitaries(); }, [loadDignitaries]);

  const openAdd = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (d: Dignitary) => {
    setEditTarget(d);
    setForm({
      name: d.name || '', title: d.title || '', rank: d.rank || 'Chief',
      position: d.position || '', category: d.category || 'Traditional Chiefs',
      village: d.village || '', photo: d.photo || '', bio: d.bio || '',
      phone: d.phone || '', email: d.email || '', whatsapp: d.whatsapp || '',
      facebook: d.facebook || '', twitter: d.twitter || '',
      isPublic: d.isPublic ?? true, displayOrder: d.displayOrder || 0,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { flash('err', 'Name is required'); return; }
    if (!form.position.trim()) { flash('err', 'Position/Role is required'); return; }
    setSaving(true);
    try {
      const url = editTarget
        ? `/api/admin/dignitaries-full/${editTarget.id}`
        : '/api/admin/dignitaries-full';
      const method = editTarget ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (d.success) {
        flash('ok', editTarget ? 'Dignitary updated.' : 'Dignitary added to address book.');
        setShowForm(false);
        loadDignitaries();
      } else { flash('err', d.error || 'Save failed'); }
    } catch { flash('err', 'Network error'); }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    try {
      const r = await fetch(`/api/admin/dignitaries-full/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await r.json();
      if (d.success) {
        flash('ok', 'Dignitary removed.');
        setDignitaries(prev => prev.filter(x => x.id !== id));
      } else { flash('err', d.error || 'Delete failed'); }
    } catch { flash('err', 'Network error'); }
    setConfirmDelete(null);
  };

  const togglePublic = async (dign: Dignitary) => {
    try {
      const r = await fetch(`/api/admin/dignitaries-full/${dign.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ ...dign, isPublic: !dign.isPublic }),
      });
      const d = await r.json();
      if (d.success) {
        setDignitaries(prev => prev.map(x => x.id === dign.id ? { ...x, isPublic: !x.isPublic } : x));
      }
    } catch { flash('err', 'Update failed'); }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = dignitaries
    .filter(d => catFilter === 'All' || d.category === catFilter)
    .filter(d => {
      if (!search) return true;
      const q = search.toLowerCase();

  const handleDignImportTemplate = () => {
    window.open('/api/dignitaries/csv-template', '_blank');
  };

  const handleDignImportUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) return;
    try {
      const text = await file.text();
      const parseRow = (line: string): string[] => {
        const r: string[] = []; let cur = ''; let inQ = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') { if (inQ && line[i+1]==='"'){cur+='"';i++;}else{inQ=!inQ;} }
          else if (ch === ',' && !inQ) { r.push(cur.trim()); cur=''; }
          else { cur += ch; }
        }
        r.push(cur.trim()); return r;
      };
      const csvLines = text.split(/\r?\n/).filter(l => l.trim());
      if (csvLines.length < 2) return;
      const headers = parseRow(csvLines[0]);
      const rows = csvLines.slice(1).map(line => {
        const vals = parseRow(line);
        const obj: Record<string,string> = {};
        headers.forEach((h,i) => { obj[h.trim()] = (vals[i]||'').trim(); });
        return obj;
      }).filter(row => Object.values(row).some(v => v));
      const r = await fetch('/api/dignitaries/import-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '') },
        body: JSON.stringify({ rows }),
      });
      const d = await r.json();
      if (d.success) { alert(d.message); window.location.reload(); }
      else { alert('Import failed: ' + d.error); }
    } catch (err: any) { alert('Error: ' + err.message); }
    e.target.value = '';
  };


      return (d.name + d.title + d.position + d.village + d.category)
        .toLowerCase().includes(q);
    })
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  // ── Dignitary Card ─────────────────────────────────────────────────────────
  const DignitaryCard: React.FC<{ d: Dignitary }> = ({ d }) => (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 14, overflow: 'hidden', position: 'relative',
      transition: 'border-color 0.2s',
    }}>
      {/* Visibility badge */}
      {isAdmin && (
        <div style={{
          position: 'absolute', top: 10, right: 10, zIndex: 2,
          padding: '2px 8px', borderRadius: 20, fontSize: 9, fontWeight: 700,
          background: d.isPublic ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
          border: `1px solid ${d.isPublic ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
          color: d.isPublic ? '#22c55e' : C.red,
        }}>
          {d.isPublic ? '● PUBLIC' : '● PRIVATE'}
        </div>
      )}

      {/* Photo / Avatar */}
      <div style={{
        height: 120, background: `linear-gradient(135deg, #0A1F14, #0F5132)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderBottom: `1px solid ${C.border}`,
      }}>
        {d.photo ? (
          <img
            src={d.photo}
            alt={d.name}
            style={{
              width: 80, height: 80, borderRadius: '50%',
              objectFit: 'cover', border: `2px solid rgba(201,168,76,0.4)`,
            }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'rgba(201,168,76,0.1)', border: `2px solid rgba(201,168,76,0.3)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Crown size={32} style={{ color: C.gold }} />
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontSize: 10, color: C.gold, fontWeight: 700,
          letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>
          {d.category}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 2 }}>
          {d.rank && d.rank !== 'Mr.' ? `${d.rank} ` : ''}{d.name}
        </div>
        {d.title && (
          <div style={{ fontSize: 11, color: C.gold, marginBottom: 4, fontStyle: 'italic' }}>
            {d.title}
          </div>
        )}
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}>
          {d.position}
        </div>
        {d.village && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 11, color: C.dim, marginBottom: 8 }}>
            <MapPin size={10} /> {d.village}
          </div>
        )}
        {d.bio && (
          <p style={{ fontSize: 11, color: C.dim, lineHeight: 1.5,
            marginBottom: 10, display: '-webkit-box', overflow: 'hidden',
            WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any }}>
            {d.bio}
          </p>
        )}

        {/* Contact Actions */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {(isAdmin && d.phone) && (
            <a href={`tel:${d.phone}`}
              style={{ display: 'flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', background: 'rgba(96,165,250,0.1)',
                border: '1px solid rgba(96,165,250,0.25)', borderRadius: 6,
                color: C.blue, fontSize: 10, fontWeight: 600, textDecoration: 'none' }}>
              <Phone size={10} /> Call
            </a>
          )}
          {(isAdmin && d.email) && (
            <a href={`mailto:${d.email}?subject=ERUE Festival 2026 — Official Communication`}
              style={{ display: 'flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', background: 'rgba(201,168,76,0.1)',
                border: '1px solid rgba(201,168,76,0.25)', borderRadius: 6,
                color: C.gold, fontSize: 10, fontWeight: 600, textDecoration: 'none' }}>
              <Mail size={10} /> Email
            </a>
          )}
          {(isAdmin && d.whatsapp) && (
            <a
              href={`https://wa.me/${d.whatsapp.replace(/\D/g, '')}?text=Dear%20${encodeURIComponent(d.rank ? d.rank + ' ' + d.name : d.name)}%2C%20Greetings%20from%20the%20ERUE%20Festival%202026%20Secretariat.`}
              target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', background: 'rgba(34,197,94,0.1)',
                border: '1px solid rgba(34,197,94,0.25)', borderRadius: 6,
                color: '#22c55e', fontSize: 10, fontWeight: 600, textDecoration: 'none' }}>
              <MessageCircle size={10} /> WhatsApp
            </a>
          )}
          {d.facebook && (
            <a href={d.facebook} target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', background: 'rgba(59,130,246,0.1)',
                border: '1px solid rgba(59,130,246,0.25)', borderRadius: 6,
                color: '#3b82f6', fontSize: 10, fontWeight: 600, textDecoration: 'none' }}>
              <ExternalLink size={10} /> Facebook
            </a>
          )}
          {d.twitter && (
            <a href={`https://x.com/${d.twitter.replace('@','')}`}
              target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', background: 'rgba(237,232,220,0.07)',
                border: `1px solid ${C.border}`, borderRadius: 6,
                color: C.muted, fontSize: 10, fontWeight: 600, textDecoration: 'none' }}>
              <ExternalLink size={10} /> X / Twitter
            </a>
          )}
        </div>

        {/* Admin Controls */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10,
            paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
            <button onClick={() => openEdit(d)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 4, padding: '6px 10px', background: C.green,
                border: `1px solid rgba(201,168,76,0.3)`, borderRadius: 6,
                color: C.gold, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              <Edit2 size={11} /> Edit
            </button>
            <button onClick={() => togglePublic(d)}
              style={{ padding: '6px 10px', background: 'none',
                border: `1px solid ${C.border}`, borderRadius: 6,
                color: d.isPublic ? '#22c55e' : C.muted,
                fontSize: 11, cursor: 'pointer' }}
              title={d.isPublic ? 'Make private' : 'Make public'}>
              {d.isPublic ? <Eye size={11} /> : <EyeOff size={11} />}
            </button>
            <button onClick={() => setConfirmDelete(d.id)}
              style={{ padding: '6px 10px', background: 'none',
                border: `1px solid rgba(239,68,68,0.3)`, borderRadius: 6,
                color: C.red, fontSize: 11, cursor: 'pointer' }}>
              <Trash2 size={11} />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ── Form Field Helper ──────────────────────────────────────────────────────
  const Field = ({ label, k, placeholder, type = 'text' }: {
    label: string; k: string; placeholder?: string; type?: string;
  }) => (
    <div>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 700,
        letterSpacing: 1, textTransform: 'uppercase', color: C.muted, marginBottom: 4 }}>
        {label}
      </label>
      <input type={type} value={(form as any)[k]} placeholder={placeholder}
        onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))}
        style={inp} />
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  // ── CSV Import handlers ────────────────────────────────────────
  const handleDignImportTemplate = () => {
    window.open('/api/dignitaries/csv-template', '_blank');
  };

  const handleDignImportUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) return;
    try {
      const text = await file.text();
      const parseRow = (ln: string): string[] => {
        const result: string[] = []; let cur = ''; let inQ = false;
        for (let ci = 0; ci < ln.length; ci++) {
          const ch = ln[ci];
          if (ch === '"') {
            if (inQ && ln[ci + 1] === '"') { cur += '"'; ci++; } else { inQ = !inQ; }
          } else if (ch === ',' && !inQ) { result.push(cur.trim()); cur = ''; }
          else { cur += ch; }
        }
        result.push(cur.trim()); return result;
      };
      const csvLines = text.split(/\r?\n/).filter((l: string) => l.trim());
      if (csvLines.length < 2) return;
      const headers = parseRow(csvLines[0]);
      const rows = csvLines.slice(1).map((line: string) => {
        const vals = parseRow(line);
        const obj: Record<string, string> = {};
        headers.forEach((h: string, idx: number) => {
          obj[h.trim()] = (vals[idx] || '').trim();
        });
        return obj;
      }).filter((row: Record<string, string>) => Object.values(row).some((v: string) => v));
      if (rows.length === 0) { alert('No valid data rows found'); return; }
      const tok = localStorage.getItem('erue_auth_token')
               || sessionStorage.getItem('erue_auth_token') || '';
      const resp = await fetch('/api/dignitaries/import-csv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tok,
        },
        body: JSON.stringify({ rows }),
      });
      const d = await resp.json();
      if (d.success) {
        alert(d.message || d.imported + ' dignitaries imported successfully');
        window.location.reload();
      } else {
        alert('Import failed: ' + (d.error || 'Unknown error'));
      }
    } catch (err: any) {
      alert('Import error: ' + err.message);
    }
    e.target.value = '';
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #060F09 0%, #0A2015 100%)',
          borderRadius: 16, padding: '28px 32px', marginBottom: 24,
          border: `1px solid rgba(201,168,76,0.2)`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8,
                  background: 'rgba(201,168,76,0.1)', border: `1px solid rgba(201,168,76,0.2)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Crown size={18} style={{ color: C.gold }} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase', color: C.gold }}>
                  ERUE Festival 2026 · Uzairue Kingdom
                </span>
              </div>
              <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: 26, fontWeight: 700,
                color: C.text, margin: 0 }}>
                Dignitaries Address Book
              </h1>
              <p style={{ fontSize: 13, color: C.muted, margin: '6px 0 0' }}>
                Distinguished guests, royal representatives, government officials & community leaders
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button onClick={loadDignitaries}
                style={{ padding: '8px 12px', background: 'none',
                  border: `1px solid ${C.border}`, borderRadius: 8,
                  color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <RefreshCw size={13} />
              </button>
              {isAdmin && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={handleDignImportTemplate}
              style={{ background: 'rgba(96,165,250,0.1)', border: '1px solid #60a5fa44', borderRadius: '8px', padding: '8px 14px', color: '#60a5fa', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              ⬇ CSV Template
            </button>
            <label style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid #C9A84C44', borderRadius: '8px', padding: '8px 14px', color: '#C9A84C', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              ⬆ Import CSV
              <input type="file" accept=".csv" style={{display:'none'}} onChange={handleDignImportUpload} />
            </label>
            <button onClick={openAdd}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 20px',
                    background: C.green, border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 10,
                    color: C.gold, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  <Plus size={15} /> Add Dignitary
                </button>
          </div>
              )}
            </div>
          </div>
        </div>

        {/* Flash */}
        {msg && (
          <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: msg.type === 'ok' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${msg.type === 'ok' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: msg.type === 'ok' ? '#22c55e' : C.red, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
            {msg.text}
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total Dignitaries', value: dignitaries.length, icon: '👑' },
            { label: 'Public Profiles', value: dignitaries.filter(d => d.isPublic).length, icon: '🌐' },
            { label: 'Categories', value: new Set(dignitaries.map(d => d.category)).size, icon: '📋' },
            { label: 'With Contact', value: dignitaries.filter(d => d.email || d.phone || d.whatsapp).length, icon: '📞' },
          ].map((stat, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.gold }}>{stat.value}</div>
              <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase',
                letterSpacing: 1 }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search + Filter */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: C.muted }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, title, village..."
              style={{ ...inp, paddingLeft: 34 }}
            />
          </div>
          <div style={{ position: 'relative' }}>
            <Filter size={13} style={{ position: 'absolute', left: 10, top: '50%',
              transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
            <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
              style={{ ...inp, paddingLeft: 30, width: 200, cursor: 'pointer' }}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['grid', 'list'] as const).map(m => (
              <button key={m} onClick={() => setViewMode(m)}
                style={{ padding: '8px 12px', background: viewMode === m ? C.green : 'none',
                  border: `1px solid ${viewMode === m ? C.gold : C.border}`, borderRadius: 7,
                  color: viewMode === m ? C.gold : C.muted, fontSize: 11, cursor: 'pointer',
                  textTransform: 'capitalize' }}>
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Count */}
        <div style={{ fontSize: 12, color: C.dim, marginBottom: 16 }}>
          Showing {filtered.length} of {dignitaries.length} dignitaries
          {catFilter !== 'All' && ` · ${catFilter}`}
          {search && ` · "${search}"`}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`,
              borderTopColor: C.gold, borderRadius: '50%', margin: '0 auto 16px',
              animation: 'spin 1s linear infinite' }} />
            <p style={{ color: C.muted, fontSize: 13 }}>Loading dignitaries...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, color: C.dim }}>
            <Crown size={48} style={{ margin: '0 auto 16px', opacity: 0.3, display: 'block' }} />
            <p style={{ fontSize: 15, color: C.muted, marginBottom: 8 }}>
              No dignitaries found
            </p>
            <p style={{ fontSize: 13, color: C.dim }}>
              {isAdmin
                ? 'Click "Add Dignitary" to begin building the address book.'
                : 'Dignitary profiles will appear here once published.'}
            </p>
            {isAdmin && (
              <button onClick={openAdd}
                style={{ marginTop: 20, padding: '10px 24px', background: C.green,
                  border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 10,
                  color: C.gold, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                + Add First Dignitary
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {filtered.map(d => <DignitaryCard key={d.id} d={d} />)}
          </div>
        ) : (
          // List view
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(d => (
              <div key={d.id} style={{ background: C.card, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: '14px 18px',
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: d.photo ? 'none' : 'rgba(201,168,76,0.1)',
                  border: `1px solid rgba(201,168,76,0.3)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden' }}>
                  {d.photo
                    ? <img src={d.photo} alt={d.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Crown size={18} style={{ color: C.gold }} />
                  }
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                    {d.rank && d.rank !== 'Mr.' ? `${d.rank} ` : ''}{d.name}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>{d.position}</div>
                  {d.village && <div style={{ fontSize: 10, color: C.dim }}>📍 {d.village}</div>}
                </div>
                <div style={{ fontSize: 10, color: C.gold, fontWeight: 600,
                  background: 'rgba(201,168,76,0.08)', padding: '3px 10px',
                  borderRadius: 20, border: `1px solid rgba(201,168,76,0.2)` }}>
                  {d.category}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {isAdmin && d.email && (
                    <a href={`mailto:${d.email}?subject=ERUE Festival 2026`}
                      style={{ padding: '5px 10px', background: 'rgba(201,168,76,0.1)',
                        border: `1px solid rgba(201,168,76,0.25)`, borderRadius: 6,
                        color: C.gold, fontSize: 10, textDecoration: 'none',
                        display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Mail size={10} /> Email
                    </a>
                  )}
                  {isAdmin && d.whatsapp && (
                    <a href={'https://wa.me/' + d.whatsapp.replace(/\D/g, '')}
                      target="_blank" rel="noreferrer"
                      style={{ padding: '5px 10px', background: 'rgba(34,197,94,0.1)',
                        border: '1px solid rgba(34,197,94,0.25)', borderRadius: 6,
                        color: '#22c55e', fontSize: 10, textDecoration: 'none',
                        display: 'flex', alignItems: 'center', gap: 3 }}>
                      <MessageCircle size={10} /> WA
                    </a>
                  )}
                  {isAdmin && (
                    <>
                      <button onClick={() => openEdit(d)}
                        style={{ padding: '5px 10px', background: C.green,
                          border: `1px solid rgba(201,168,76,0.3)`, borderRadius: 6,
                          color: C.gold, fontSize: 10, cursor: 'pointer' }}>
                        <Edit2 size={10} />
                      </button>
                      <button onClick={() => setConfirmDelete(d.id)}
                        style={{ padding: '5px 10px', background: 'none',
                          border: `1px solid rgba(239,68,68,0.3)`, borderRadius: 6,
                          color: C.red, fontSize: 10, cursor: 'pointer' }}>
                        <Trash2 size={10} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Add / Edit Form Modal ────────────────────────────────────────────── */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          zIndex: 1000, display: 'flex', alignItems: 'flex-start',
          justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
          <div style={{ background: '#0E1812', border: `1px solid ${C.border}`,
            borderRadius: 16, padding: 28, width: '100%', maxWidth: 700,
            margin: '20px auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 22 }}>
              <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.gold, margin: 0 }}>
                {editTarget ? '✏️ Edit Dignitary' : '+ Add Dignitary'}
              </h2>
              <button onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', color: C.muted,
                  fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {/* Row 1: Name + Rank */}
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase', color: C.muted, marginBottom: 4 }}>
                  Full Name *
                </label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Emmanuel Obaseki" style={inp} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase', color: C.muted, marginBottom: 4 }}>
                  Honorific Rank
                </label>
                <select value={form.rank} onChange={e => setForm(p => ({ ...p, rank: e.target.value }))}
                  style={{ ...inp, cursor: 'pointer' }}>
                  {RANKS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              {/* Row 2: Title + Category */}
              <Field label="Title / Style" k="title" placeholder="e.g. Enogie of Uzairue" />
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase', color: C.muted, marginBottom: 4 }}>
                  Category *
                </label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  style={{ ...inp, cursor: 'pointer' }}>
                  {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Row 3: Position + Village */}
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Position / Role *" k="position" placeholder="e.g. Governor, Edo State" />
              </div>
              <Field label="Village / LGA / State" k="village" placeholder="e.g. Jattu-Uzairue" />
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase', color: C.muted, marginBottom: 4 }}>
                  Display Order
                </label>
                <input type="number" min="0" value={form.displayOrder}
                  onChange={e => setForm(p => ({ ...p, displayOrder: parseInt(e.target.value) || 0 }))}
                  style={inp} />
              </div>

              {/* Photo */}
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Photo URL (Cloudinary)" k="photo"
                  placeholder="https://res.cloudinary.com/uuovmrek/image/upload/..." />
              </div>

              {/* Bio */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase', color: C.muted, marginBottom: 4 }}>
                  Biography
                </label>
                <textarea value={form.bio} rows={3}
                  onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                  placeholder="Brief biography or description..."
                  style={{ ...inp, resize: 'vertical' }} />
              </div>

              {/* Separator */}
              <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${C.border}`,
                paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: C.gold, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
                  📞 Contact Details (Admin Only — Never Public)
                </div>
              </div>

              <Field label="Phone" k="phone" placeholder="+234 800 000 0000" />
              <Field label="Email" k="email" placeholder="name@domain.com" type="email" />
              <Field label="WhatsApp Number" k="whatsapp" placeholder="+2348000000000" />
              <Field label="Facebook URL" k="facebook" placeholder="https://facebook.com/..." />
              <Field label="X / Twitter Handle" k="twitter" placeholder="@handle" />

              {/* Public toggle */}
              <div style={{ gridColumn: '1 / -1', display: 'flex',
                alignItems: 'center', gap: 12, paddingTop: 4 }}>
                <button onClick={() => setForm(p => ({ ...p, isPublic: !p.isPublic }))}
                  style={{ width: 44, height: 24, borderRadius: 12, border: 'none',
                    background: form.isPublic ? '#22c55e' : C.border, cursor: 'pointer',
                    position: 'relative', transition: 'background 0.2s' }}>
                  <div style={{ position: 'absolute', top: 3,
                    left: form.isPublic ? 22 : 3,
                    width: 18, height: 18, borderRadius: '50%',
                    background: '#fff', transition: 'left 0.2s' }} />
                </button>
                <span style={{ fontSize: 13, color: C.muted }}>
                  {form.isPublic
                    ? '🌐 Visible on public website'
                    : '🔒 Private — admin view only'}
                </span>
              </div>
            </div>

            {/* Save / Cancel */}
            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1, padding: '11px 20px', background: C.green,
                  border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 9,
                  color: C.gold, fontSize: 13, fontWeight: 700,
                  cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : editTarget ? '✅ Update Dignitary' : '+ Add to Address Book'}
              </button>
              <button onClick={() => setShowForm(false)}
                style={{ padding: '11px 20px', background: 'transparent',
                  border: `1px solid ${C.border}`, borderRadius: 9,
                  color: C.muted, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ─────────────────────────────────────────────── */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0E1812', border: `1px solid rgba(239,68,68,0.3)`,
            borderRadius: 14, padding: 28, maxWidth: 380, width: '90%', textAlign: 'center' }}>
            <Trash2 size={32} style={{ color: C.red, margin: '0 auto 14px', display: 'block' }} />
            <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 16, color: C.text, marginBottom: 8 }}>
              Remove Dignitary?
            </h3>
            <p style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              This will permanently remove this dignitary from the address book.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => handleDelete(confirmDelete)}
                style={{ padding: '9px 24px', background: 'rgba(239,68,68,0.15)',
                  border: `1px solid rgba(239,68,68,0.4)`, borderRadius: 8,
                  color: C.red, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Yes, Remove
              </button>
              <button onClick={() => setConfirmDelete(null)}
                style={{ padding: '9px 24px', background: 'none',
                  border: `1px solid ${C.border}`, borderRadius: 8,
                  color: C.muted, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DiagnitariesBook;
